import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Store from '../../../redux/Store';
import { Lens as CircleIcon, GetApp as DownloadIcon } from '@mui/icons-material';
import { IconButton, Tooltip, Zoom } from '@mui/material';
import Constants from '../../../common/constants/Constants';
import DeploymentStateEnum from '../../../common/constants/DeploymentStateEnum';
import Loading from '../../../common/components/Loading/Loading';
import CommandJobCreator from './CommandJobCreator';
import { getFleets, getPackages, deployPackage } from './CommandJobActions';

import { getCommandJobDetails, getReadOnlyBlobSasToken } from './CommandJobActions';
import { getInstrumentsListForFleet } from '../../Fleets/components/FleetEditor/FleetEditor.actions';

import { showSnackbar } from '../../../common/state/snackbar/Snackbar.actions';

import PermissionsCheck from '../../../common/components/PermissionsCheck/PermissionsCheck';
import PermissionsEnum from '../../../common/constants/PermissionsEnum';
import PageContainer from '../../../common/components/Page/PageContainer';

function CommandJobCreatorContainer(props) {
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [canStartCommandJob, setCanStartCommandJob] = useState(true);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [selectedFleetId, setSelectedFleetId] = useState('');
  const [fleetList, setFleetList] = useState([]);
  const [packageList, setPackageList] = useState([]);

  let earlyMessages = [];
  const [columnConfigs, setColumnConfigs] = useState([
    {
      name: 'Serial number',
      key: 'SerialNumber',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Type',
      key: 'Type',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Region',
      key: 'RegionName',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Group',
      key: 'GroupName',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Location',
      key: 'Location',
      type: 'string',
      visible: false,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Software version',
      key: 'SoftwareVersion',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Date and time of last command job status',
      key: 'LastUpdateJobDateTime',
      type: 'datetime',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Command job status',
      key: 'CommandJobStatus',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Error message',
      key: 'ErrorMessage',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Connection alert',
      key: 'ConnectionAlert.Level',
      type: 'markup',
      markup: (el, col, elIndex, colIndex) => {
        var style = {
          width: '.75rem',
          height: '.75rem',
          marginTop: '5px',
          color: el.ConnectionAlert ? el.ConnectionAlert.Color : Constants.COLORS.NEUTRAL,
        };
        return <CircleIcon key={1} style={style} fill={style.color} />;
      },
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Actions',
      key: 'Actions',
      type: 'markup',
      markup: (el, col, elIndex, colIndex) => {
        return (
          <div>
            <Tooltip disableInteractive TransitionComponent={Zoom} title="Download result">
              <IconButton
                size="large"
                key={1}
                style={{}}
                onClick={() => {
                  return _onClickDownload(el);
                }}
                data-qa="qa-download"
                disabled={el.DeploymentState != DeploymentStateEnum.EXECUTION_COMPLETED}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
      visible: true,
      filterable: false,
      sortable: false,
    },
  ]);

  useEffect(() => {
    let fleetList = null;
    let packageList = null;

    Promise.all([getFleets(), getPackages()])
      .then(([resFleets, resPackages]) => {
        fleetList = resFleets.body;
        packageList = resPackages.body;
        setFleetList(fleetList);
        setPackageList(packageList);
        setIsFetching(false);
      })
      .catch((err) => setIsFetching(false));
  }, []);

  useEffect(() => {
    if (props && props.packageDeploymentMessage) {
      _handlePackageDeploymentMessage(props.packageDeploymentMessage);
    }
  }, [props.packageDeploymentMessage]);

  useEffect(() => {
    if (props && props.transferProgressMessage) {
      _handleTransferProgressMessage(props.transferProgressMessage);
    }
  }, [props.transferProgressMessage]);

  const _sortByName = (array) => {
    return array.sort((a, b) => a.Name.localeCompare(b.Name));
  };

  const _handlePackageDeploymentMessage = (message) => {
    const { CommandGuid, DeploymentState, ErrorMessage, DeploymentStateTime } = JSON.parse(
      message.data
    );

    let newData = Object.assign([], data);
    const elIndex = newData.findIndex((el) => el.MessageCommandGuid === CommandGuid);

    if (elIndex === -1) {
      earlyMessages.push(message);
      return;
    }

    newData[elIndex] = {
      ...newData[elIndex],
      CommandJobStatus: DeploymentStateEnum.GetStateName(DeploymentState),
      DeploymentState,
      ErrorMessage: ErrorMessage,
      LastCommandJobDateTime: DeploymentStateTime,
    };
    setData(newData);
  };

  const _retrieveEarlierMessages = () => {
    if (earlyMessages.length > 0) {
      earlyMessages.forEach((message) => _handlePackageDeploymentMessage(message));
      earlyMessages = [];
    }
  };

  const _handleTransferProgressMessage = (message) => {
    const { CommandGuid, FileSize, BytesTransferred, Status } = JSON.parse(message.data);
    let newData = Object.assign([], data);
    let elIndex = newData.findIndex((el) => el.MessageCommandGuid === CommandGuid);
    if (elIndex === -1) return;
    const percentage = Math.round((BytesTransferred / FileSize) * 100);
    const labeledStatus = Status + ' ' + percentage + '%';

    newData[elIndex] = {
      ...newData[elIndex],
      CommandJobStatus: labeledStatus,
    };
    setData(newData);
  };

  const _onSelectPackageCallback = (value) => {
    setSelectedPackageId(value);
  };

  const _onSelectFleetCallback = (value) => {
    getInstrumentsListForFleet(value)
      .then((res) => {
        // Initialize properties, since the response doesn't contain it (see PackageDeploymentManager.GetCommandJobDetails)
        let instrumentList = res.body.Data.map((i) => ({
          ...i,
          MessageCommandGuid: '',
          CommandJobStatus: DeploymentStateEnum.GetStateName(DeploymentStateEnum.NONE),
          LastCommandJobDateTime: null,
          ErrorMessage: '',
          Error: false,
        }));
        setSelectedFleetId(value);
        setData(instrumentList);
      })
      .catch((err) => {});
  };

  const _startCommandJob = () => {
    setCanStartCommandJob(false);
    let createdJobId = null;

    deployPackage(selectedPackageId, selectedFleetId)
      .then((res) => {
        createdJobId = res.body.PackageDeploymentJobId;
        setCanStartCommandJob(true);
        Store.dispatch(
          showSnackbar({
            type: Constants.SNACKBAR_MESSAGE_TYPES.SUCCESS,
            message: 'Command job successfully started.',
          })
        );
      })
      .then(() => _getDeployments(createdJobId))
      .catch((err) => {
        setCanStartCommandJob(true);
        Store.dispatch(
          showSnackbar({
            type: Constants.SNACKBAR_MESSAGE_TYPES.ERROR,
            message: 'Failed to start update job.',
          })
        );
      });
  };

  const _validateRequiredSelection = () => {
    if (selectedFleetId && selectedPackageId && canStartCommandJob && data.length > 0) {
      return true;
    }
    return false;
  };

  const _getDeployments = (createdJobId) => {
    getCommandJobDetails(createdJobId)
      .then((res) => {
        let newData = res.body;
        newData.forEach(
          (n) => (n.CommandJobStatus = DeploymentStateEnum.GetStateName(n.CommandJobStatus))
        );
        const mergedData = Object.assign(data, newData);
        setData(mergedData);
        setIsFetching(false);
        _retrieveEarlierMessages();
      })
      .catch((err) => setIsFetching(false));
  };

  const _onClickDownload = (row) => {
    getReadOnlyBlobSasToken(row.SerialNumber, row.MessageCommandGuid)
      .then((res) => {
        let blobUrlWithToken = res.body.storageUrlWithBlobSasToken;
        var downloadWindow = window.open(blobUrlWithToken);
        try {
          downloadWindow.focus();
        } catch (e) {
          Store.dispatch(
            showSnackbar({
              type: Constants.SNACKBAR_MESSAGE_TYPES.ERROR,
              message:
                'Download blocked by browser. Add this site to your exception list and try again.',
            })
          );
        }
      })
      .catch((err) => {
        Store.dispatch(
          showSnackbar({
            type: Constants.SNACKBAR_MESSAGE_TYPES.ERROR,
            message: 'Failed to download the selected file.',
          })
        );
      });
  };

  return (
    <PageContainer pageName="update-job-creator">
      <PermissionsCheck appliedToPage={true} permission={PermissionsEnum.CREATE_UPDATE_JOBS}>
        <Loading condition={isFetching}>
          <div>
            <CommandJobCreator
              availablePackages={_sortByName(packageList)}
              fleets={_sortByName(fleetList)}
              selectedPackageId={selectedPackageId}
              selectedFleetId={selectedFleetId}
              onSelectPackage={_onSelectPackageCallback}
              onSelectFleet={_onSelectFleetCallback}
              startCommandJob={_startCommandJob}
              startCommandJobDisabled={!_validateRequiredSelection()}
              columns={columnConfigs}
              data={data}
            />
          </div>
        </Loading>
      </PermissionsCheck>
    </PageContainer>
  );
}

const mapStateToProps = (state) => ({
  packageDeploymentMessage:
    state.push.pushMessages != null
      ? state.push.pushMessages.find((el) => el.type === 'PackageDeploymentFeedback')
      : null,
  transferProgressMessage:
    state.push.pushMessages != null
      ? state.push.pushMessages.find((el) => el.type === 'FileTransferProgress')
      : null,
});

export default connect(mapStateToProps)(CommandJobCreatorContainer);
